import React, { useState, useEffect } from 'react';
import {
    Container,
    Typography,
    Button,
    Paper,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Box,
    Chip,
    Divider,
    useTheme,
    Grid,
    Slide,
    MenuItem,
    InputLabel,
    FormControl,
    Select,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Card,
    CardContent,
    Tabs,
    Tab,
    Alert,
    LinearProgress,
    CircularProgress,
    Tooltip,
    Menu,
    Avatar,
    List,
    ListItem,
    ListItemText,
    ListItemAvatar,
    Rating
} from '@mui/material';
import {
    PictureAsPdf,
    TableChart,
    GetApp,
    FilterList,
    Refresh,
    Today,
    Close,
    Search,
    Download,
    TrendingUp,
    TrendingDown,
    Inventory,
    AttachMoney,
    Storage,
    Warning,
    CheckCircle,
    Error,
    CalendarMonth,
    BarChart,
    PieChart,
    Summarize,
    ShoppingCart,
    Store,
    LocalShipping,
    Category,
    Assessment,
    Analytics,
    Hotel,
    Person,
    Business,
    People,
    EventSeat,
    MeetingRoom,
    Payment,
    CreditCard,
    AccountBalance,
    Phone,
    Email,
    LocationOn,
    AccessTime,
    Event,
    Star,
    StarBorder
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import axios from 'axios';
import useCustomToast from '../../hooks/useToast';
import {
    APIBookingReport,
    APIBookingStats,
    APIRoomNames,
    APIBookingSourceNames,
    APIBookingTypeNames
} from '../../services/APIs';
import { TEXT_CONNECTION_ERROR } from '../../services/Text';
import {
    formatNumberWithComma,
    getCategoriesNameById,
    formatCurrency,
    formatCreatedAtDate,
    getStatusById
} from '../../utils/helpers';
import exportBookingReport from '../../services/reservation/BookingReportService';
import { getUserRole, ROLE_PERMISSIONS } from '../../utils/Permission';

const BookingReport = () => {
    const theme = useTheme();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const showToast = useCustomToast();
    const [exportAnchorEl, setExportAnchorEl] = useState(null);

    const [reportData, setReportData] = useState({
        summary: {
            total_bookings: 0,
            total_revenue: 0,
            total_advance: 0,
            total_balance: 0,
            average_booking_value: 0,
            occupancy_rate: 0,
            cancelled_bookings: 0,
            checked_in_bookings: 0,
            checked_out_bookings: 0,
            active_bookings: 0,
            individual_bookings: 0,
            organisation_bookings: 0,
            unique_customers: 0,
            unique_organisations: 0
        },
        details: [],
        revenue_by_status: [],
        bookings_by_type: [],
        top_customers: [],
        top_organisations: [],
        recent_activity: [],
        monthly_trends: []
    });

    const [filters, setFilters] = useState({
        status: '',
        booking_type: '',
        payment_mode: '',
        customer_name: '',
        booking_no: '',
        room_type: '',
        dateFrom: '',
        dateTo: '',
        checkInFrom: '',
        checkInTo: '',
        checkOutFrom: '',
        checkOutTo: '',
        min_amount: '',
        max_amount: '',
        min_advance: '',
        max_advance: '',
        report_type: 'detailed',
        sort_by: 'created_at',
        sort_order: 'desc'
    });

    const [referenceData, setReferenceData] = useState({
        roomNames: [],
        bookingSourceNames: [],
        bookingTypeNames: [],
        paymentModes: [
            { id: 'cash', name: 'Cash' },
            { id: 'card', name: 'Card' },
            { id: 'mobile', name: 'Mobile Money' },
            { id: 'bank', name: 'Bank Transfer' },
            { id: 'credit', name: 'Credit' }
        ]
    });

    const [activeTab, setActiveTab] = useState(0);
    const [userRole, setUserRole] = useState('');

    useEffect(() => {
        const role = getUserRole();
        setUserRole(role);
        fetchReferenceData();
        generateReport();
    }, []);

    const hasPermission = (permission) => {
        return ROLE_PERMISSIONS[userRole]?.includes(permission) || false;
    };

    const fetchReferenceData = async () => {
        try {
            // Fetch all reference data in parallel
            const [roomsResult, sourcesResult, typesResult] = await Promise.all([
                axios.get(APIRoomNames).catch(() => ({ data: { data: [] } })),
                axios.get(APIBookingSourceNames).catch(() => ({ data: { data: [] } })),
                axios.get(APIBookingTypeNames).catch(() => ({ data: { data: [] } }))
            ]);

            setReferenceData({
                roomNames: roomsResult.data?.data || [],
                bookingSourceNames: sourcesResult.data?.data || [],
                bookingTypeNames: typesResult.data?.data || [],
                paymentModes: referenceData.paymentModes
            });
        } catch (error) {
            console.error("Error fetching reference data:", error);
            showToast("Error", "Failed to load some reference data", "warning");
        }
    };

    const generateReport = async () => {
        setLoading(true);
        setError(null);

        try {
            const params = { ...filters };

            // Clean up empty params
            Object.keys(params).forEach(key => {
                if (!params[key] && params[key] !== 0) delete params[key];
            });

            const response = await axios.get(APIBookingReport, { params });

            if (response.data.status === 'success') {
                setReportData(response.data.data);
                showToast("Success", "Report generated successfully", "success");
            } else {
                throw new Error(response.data.message || "Failed to generate report");
            }
        } catch (error) {
            let errorMessage = "Failed to generate report";
            if (error.response) {
                errorMessage = error.response.data.message || error.message;
            } else if (error.code === "ERR_NETWORK") {
                errorMessage = TEXT_CONNECTION_ERROR;
            }
            setError(errorMessage);
            showToast("Error", errorMessage, "error");
        } finally {
            setLoading(false);
        }
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleDateRangeChange = (type, value) => {
        setFilters(prev => ({
            ...prev,
            [type]: value
        }));
    };

    const applyToday = () => {
        const today = new Date().toISOString().split('T')[0];
        setFilters({
            ...filters,
            dateFrom: today,
            dateTo: today
        });
    };

    const applyThisWeek = () => {
        const today = new Date();
        const firstDay = new Date(today.setDate(today.getDate() - today.getDay()));
        const lastDay = new Date(today.setDate(today.getDate() - today.getDay() + 6));

        setFilters({
            ...filters,
            dateFrom: firstDay.toISOString().split('T')[0],
            dateTo: lastDay.toISOString().split('T')[0]
        });
    };

    const applyThisMonth = () => {
        const today = new Date();
        const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
        const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);

        setFilters({
            ...filters,
            dateFrom: firstDay.toISOString().split('T')[0],
            dateTo: lastDay.toISOString().split('T')[0]
        });
    };

    const applyLastMonth = () => {
        const today = new Date();
        const firstDay = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const lastDay = new Date(today.getFullYear(), today.getMonth(), 0);

        setFilters({
            ...filters,
            dateFrom: firstDay.toISOString().split('T')[0],
            dateTo: lastDay.toISOString().split('T')[0]
        });
    };

    const clearFilters = () => {
        setFilters({
            status: '',
            booking_type: '',
            payment_mode: '',
            customer_name: '',
            booking_no: '',
            room_type: '',
            dateFrom: '',
            dateTo: '',
            checkInFrom: '',
            checkInTo: '',
            checkOutFrom: '',
            checkOutTo: '',
            min_amount: '',
            max_amount: '',
            min_advance: '',
            max_advance: '',
            report_type: 'detailed',
            sort_by: 'created_at',
            sort_order: 'desc'
        });
    };

    const handleExportClick = (event) => {
        setExportAnchorEl(event.currentTarget);
    };

    const handleExportClose = () => {
        setExportAnchorEl(null);
    };

    const handleExportExcel = async () => {
        try {
            handleExportClose();
            const result = await exportBookingReport.toExcel(reportData, filters, referenceData);
            showToast("Success", `Excel report downloaded: ${result.fileName}`, "success");
        } catch (error) {
            showToast("Error", error.message, "error");
        }
    };

    const handleExportPDF = async () => {
        try {
            handleExportClose();
            await exportBookingReport.toPDF(reportData, filters, referenceData);
            showToast("Success", "PDF report downloaded successfully", "success");
        } catch (error) {
            console.error('PDF export error:', error);
            showToast("Error", error.message, "error");
        }
    };

    const handleExportCSV = async () => {
        try {
            handleExportClose();
            await exportBookingReport.toCSV(reportData, filters, referenceData);
            showToast("Success", "CSV report downloaded successfully", "success");
        } catch (error) {
            console.error('CSV export error:', error);
            showToast("Error", error.message, "error");
        }
    };

    const getStatusColor = (status) => {
        switch (parseInt(status)) {
            case 0: return theme.palette.error.main;
            case 1: return theme.palette.primary.main;
            case 2: return theme.palette.success.main;
            case 3: return theme.palette.info.main;
            default: return theme.palette.grey[500];
        }
    };

    const getPaymentStatusColor = (booking) => {
        const balance = (booking.total_amount || 0) - (booking.advance_amount || 0);
        if (balance <= 0) return theme.palette.success.main;
        if (booking.advance_amount > 0) return theme.palette.warning.main;
        return theme.palette.error.main;
    };

    const getPaymentStatusText = (booking) => {
        const balance = (booking.total_amount || 0) - (booking.advance_amount || 0);
        if (balance <= 0) return 'Paid';
        if (booking.advance_amount > 0) return 'Partial';
        return 'Pending';
    };

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    const getBookingTypeIcon = (type) => {
        return type === 1 ? <Person /> : <Business />;
    };

    const getCustomerDisplay = (booking) => {
        if (booking.booking_type === 2) {
            return booking.organisation_name || 'N/A';
        }
        return booking.customer_name || 'N/A';
    };

    const getCustomerContact = (booking) => {
        if (booking.booking_type === 2) {
            if (booking.organisation_members && booking.organisation_members.length > 0) {
                return booking.organisation_members[0].phone || 'N/A';
            }
            return 'N/A';
        }
        return booking.customer_mobile || 'N/A';
    };

    if (loading && !reportData.details.length) {
        return (
            <Container maxWidth="xl" sx={{ py: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                <Box textAlign="center">
                    <CircularProgress size={60} />
                    <Typography variant="h6" sx={{ mt: 2 }}>Generating Booking Report...</Typography>
                </Box>
            </Container>
        );
    }

    return (
        <Container maxWidth="xl" sx={{ py: 3 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                <Box>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                        Booking Analysis Report
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Comprehensive booking analysis with customer and revenue insights
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<Refresh />}
                        onClick={generateReport}
                    >
                        Refresh Report
                    </Button>
                    <Button
                        variant="contained"
                        color="success"
                        startIcon={<GetApp />}
                        onClick={handleExportClick}
                    >
                        Export Report
                    </Button>
                </Box>
            </Box>

            {/* Filters Section */}
            <Paper elevation={2} sx={{ p: 3, mb: 4, borderRadius: 3 }}>
                <Typography variant="h6" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FilterList /> Report Filters
                </Typography>

                <Grid container spacing={3}>
                    {/* Quick Date Filters */}
                    <Grid item xs={12}>
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                            <Button
                                variant="outlined"
                                startIcon={<Today />}
                                onClick={applyToday}
                            >
                                Today
                            </Button>
                            <Button
                                variant="outlined"
                                startIcon={<CalendarMonth />}
                                onClick={applyThisWeek}
                            >
                                This Week
                            </Button>
                            <Button
                                variant="outlined"
                                startIcon={<CalendarMonth />}
                                onClick={applyThisMonth}
                            >
                                This Month
                            </Button>
                            <Button
                                variant="outlined"
                                startIcon={<CalendarMonth />}
                                onClick={applyLastMonth}
                            >
                                Last Month
                            </Button>
                            <Button
                                variant="outlined"
                                color="error"
                                startIcon={<Close />}
                                onClick={clearFilters}
                            >
                                Clear All
                            </Button>
                        </Box>
                    </Grid>

                    {/* Status Filter */}
                    <Grid item xs={12} md={6} lg={3}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Booking Status</InputLabel>
                            <Select
                                name="status"
                                value={filters.status}
                                onChange={handleFilterChange}
                                label="Booking Status"
                            >
                                <MenuItem value="">All Status</MenuItem>
                                <MenuItem value="0">Cancelled</MenuItem>
                                <MenuItem value="1">Active</MenuItem>
                                <MenuItem value="2">Checked In</MenuItem>
                                <MenuItem value="3">Checked Out</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>

                    {/* Booking Type Filter */}
                    <Grid item xs={12} md={6} lg={3}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Booking Type</InputLabel>
                            <Select
                                name="booking_type"
                                value={filters.booking_type}
                                onChange={handleFilterChange}
                                label="Booking Type"
                            >
                                <MenuItem value="">All Types</MenuItem>
                                <MenuItem value="1">Individual</MenuItem>
                                <MenuItem value="2">Organisation</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>

                    {/* Payment Mode Filter */}
                    <Grid item xs={12} md={6} lg={3}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Payment Mode</InputLabel>
                            <Select
                                name="payment_mode"
                                value={filters.payment_mode}
                                onChange={handleFilterChange}
                                label="Payment Mode"
                            >
                                <MenuItem value="">All Modes</MenuItem>
                                {referenceData.paymentModes.map((mode) => (
                                    <MenuItem key={mode.id} value={mode.id}>
                                        {mode.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    {/* Room Type Filter */}
                    <Grid item xs={12} md={6} lg={3}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Room Type</InputLabel>
                            <Select
                                name="room_type"
                                value={filters.room_type}
                                onChange={handleFilterChange}
                                label="Room Type"
                            >
                                <MenuItem value="">All Rooms</MenuItem>
                                {referenceData.roomNames.map((room) => (
                                    <MenuItem key={room.id} value={room.id}>
                                        {room.roomtype || room.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    {/* Booking Number Search */}
                    <Grid item xs={12} md={6} lg={3}>
                        <TextField
                            fullWidth
                            label="Booking Number"
                            name="booking_no"
                            value={filters.booking_no}
                            onChange={handleFilterChange}
                            size="small"
                            placeholder="Search by booking number"
                        />
                    </Grid>

                    {/* Customer/Organisation Name Search */}
                    <Grid item xs={12} md={6} lg={3}>
                        <TextField
                            fullWidth
                            label="Customer/Organisation Name"
                            name="customer_name"
                            value={filters.customer_name}
                            onChange={handleFilterChange}
                            size="small"
                            placeholder="Search by name"
                        />
                    </Grid>

                    {/* Check-in Date Range */}
                    <Grid item xs={12} md={6} lg={3}>
                        <TextField
                            fullWidth
                            label="Check-in From"
                            type="date"
                            name="checkInFrom"
                            value={filters.checkInFrom}
                            onChange={(e) => handleDateRangeChange('checkInFrom', e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            size="small"
                        />
                    </Grid>
                    <Grid item xs={12} md={6} lg={3}>
                        <TextField
                            fullWidth
                            label="Check-in To"
                            type="date"
                            name="checkInTo"
                            value={filters.checkInTo}
                            onChange={(e) => handleDateRangeChange('checkInTo', e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            size="small"
                        />
                    </Grid>

                    {/* Check-out Date Range */}
                    <Grid item xs={12} md={6} lg={3}>
                        <TextField
                            fullWidth
                            label="Check-out From"
                            type="date"
                            name="checkOutFrom"
                            value={filters.checkOutFrom}
                            onChange={(e) => handleDateRangeChange('checkOutFrom', e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            size="small"
                        />
                    </Grid>
                    <Grid item xs={12} md={6} lg={3}>
                        <TextField
                            fullWidth
                            label="Check-out To"
                            type="date"
                            name="checkOutTo"
                            value={filters.checkOutTo}
                            onChange={(e) => handleDateRangeChange('checkOutTo', e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            size="small"
                        />
                    </Grid>

                    {/* Amount Range */}
                    <Grid item xs={12} md={6} lg={3}>
                        <TextField
                            fullWidth
                            label="Min Amount"
                            type="number"
                            name="min_amount"
                            value={filters.min_amount}
                            onChange={handleFilterChange}
                            size="small"
                            InputProps={{ inputProps: { min: 0, step: "0.01" } }}
                        />
                    </Grid>
                    <Grid item xs={12} md={6} lg={3}>
                        <TextField
                            fullWidth
                            label="Max Amount"
                            type="number"
                            name="max_amount"
                            value={filters.max_amount}
                            onChange={handleFilterChange}
                            size="small"
                            InputProps={{ inputProps: { min: 0, step: "0.01" } }}
                        />
                    </Grid>

                    {/* Advance Amount Range */}
                    <Grid item xs={12} md={6} lg={3}>
                        <TextField
                            fullWidth
                            label="Min Advance"
                            type="number"
                            name="min_advance"
                            value={filters.min_advance}
                            onChange={handleFilterChange}
                            size="small"
                            InputProps={{ inputProps: { min: 0, step: "0.01" } }}
                        />
                    </Grid>
                    <Grid item xs={12} md={6} lg={3}>
                        <TextField
                            fullWidth
                            label="Max Advance"
                            type="number"
                            name="max_advance"
                            value={filters.max_advance}
                            onChange={handleFilterChange}
                            size="small"
                            InputProps={{ inputProps: { min: 0, step: "0.01" } }}
                        />
                    </Grid>

                    {/* Creation Date Range */}
                    <Grid item xs={12} md={6} lg={3}>
                        <TextField
                            fullWidth
                            label="Created From"
                            type="date"
                            name="dateFrom"
                            value={filters.dateFrom}
                            onChange={(e) => handleDateRangeChange('dateFrom', e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            size="small"
                        />
                    </Grid>
                    <Grid item xs={12} md={6} lg={3}>
                        <TextField
                            fullWidth
                            label="Created To"
                            type="date"
                            name="dateTo"
                            value={filters.dateTo}
                            onChange={(e) => handleDateRangeChange('dateTo', e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            size="small"
                        />
                    </Grid>

                    {/* Report Type */}
                    <Grid item xs={12} md={6} lg={2}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Report Type</InputLabel>
                            <Select
                                name="report_type"
                                value={filters.report_type}
                                onChange={handleFilterChange}
                                label="Report Type"
                            >
                                <MenuItem value="detailed">Detailed</MenuItem>
                                <MenuItem value="summary">Summary</MenuItem>
                                <MenuItem value="customer">By Customer</MenuItem>
                                <MenuItem value="organisation">By Organisation</MenuItem>
                                <MenuItem value="revenue">Revenue</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>

                    {/* Sort Options */}
                    <Grid item xs={12} md={6} lg={2}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Sort By</InputLabel>
                            <Select
                                name="sort_by"
                                value={filters.sort_by}
                                onChange={handleFilterChange}
                                label="Sort By"
                            >
                                <MenuItem value="created_at">Created Date</MenuItem>
                                <MenuItem value="check_in">Check-in Date</MenuItem>
                                <MenuItem value="check_out">Check-out Date</MenuItem>
                                <MenuItem value="total_amount">Total Amount</MenuItem>
                                <MenuItem value="advance_amount">Advance Amount</MenuItem>
                                <MenuItem value="booking_type">Booking Type</MenuItem>
                                <MenuItem value="status">Status</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item xs={12} md={6} lg={2}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Sort Order</InputLabel>
                            <Select
                                name="sort_order"
                                value={filters.sort_order}
                                onChange={handleFilterChange}
                                label="Sort Order"
                            >
                                <MenuItem value="desc">Descending</MenuItem>
                                <MenuItem value="asc">Ascending</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>

                    {/* Generate Button */}
                    <Grid item xs={12}>
                        <Button
                            fullWidth
                            variant="contained"
                            color="primary"
                            size="large"
                            onClick={generateReport}
                            startIcon={<Search />}
                            sx={{ mt: 2 }}
                        >
                            Generate Report
                        </Button>
                    </Grid>
                </Grid>

                {/* Active Filters */}
                {Object.keys(filters).some(key => filters[key] && !['report_type', 'sort_by', 'sort_order'].includes(key)) && (
                    <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                            Active Filters:
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            {filters.status && (
                                <Chip
                                    label={`Status: ${getStatusById(filters.status)}`}
                                    onDelete={() => handleFilterChange({ target: { name: 'status', value: '' } })}
                                    color="primary"
                                    size="small"
                                />
                            )}
                            {filters.booking_type && (
                                <Chip
                                    label={`Type: ${filters.booking_type === '1' ? 'Individual' : 'Organisation'}`}
                                    onDelete={() => handleFilterChange({ target: { name: 'booking_type', value: '' } })}
                                    color="secondary"
                                    size="small"
                                />
                            )}
                            {filters.payment_mode && (
                                <Chip
                                    label={`Payment: ${filters.payment_mode}`}
                                    onDelete={() => handleFilterChange({ target: { name: 'payment_mode', value: '' } })}
                                    color="info"
                                    size="small"
                                />
                            )}
                            {filters.room_type && (
                                <Chip
                                    label={`Room: ${getCategoriesNameById(filters.room_type, referenceData.roomNames)}`}
                                    onDelete={() => handleFilterChange({ target: { name: 'room_type', value: '' } })}
                                    color="warning"
                                    size="small"
                                />
                            )}
                            {filters.booking_no && (
                                <Chip
                                    label={`Booking #: ${filters.booking_no}`}
                                    onDelete={() => handleFilterChange({ target: { name: 'booking_no', value: '' } })}
                                    color="success"
                                    size="small"
                                />
                            )}
                            {filters.customer_name && (
                                <Chip
                                    label={`Customer: ${filters.customer_name}`}
                                    onDelete={() => handleFilterChange({ target: { name: 'customer_name', value: '' } })}
                                    color="primary"
                                    size="small"
                                />
                            )}
                            {filters.checkInFrom && filters.checkInTo && (
                                <Chip
                                    label={`Check-in: ${filters.checkInFrom} to ${filters.checkInTo}`}
                                    onDelete={() => {
                                        handleFilterChange({ target: { name: 'checkInFrom', value: '' } });
                                        handleFilterChange({ target: { name: 'checkInTo', value: '' } });
                                    }}
                                    color="info"
                                    size="small"
                                />
                            )}
                            {filters.checkOutFrom && filters.checkOutTo && (
                                <Chip
                                    label={`Check-out: ${filters.checkOutFrom} to ${filters.checkOutTo}`}
                                    onDelete={() => {
                                        handleFilterChange({ target: { name: 'checkOutFrom', value: '' } });
                                        handleFilterChange({ target: { name: 'checkOutTo', value: '' } });
                                    }}
                                    color="warning"
                                    size="small"
                                />
                            )}
                            {filters.dateFrom && filters.dateTo && (
                                <Chip
                                    label={`Created: ${filters.dateFrom} to ${filters.dateTo}`}
                                    onDelete={() => {
                                        handleFilterChange({ target: { name: 'dateFrom', value: '' } });
                                        handleFilterChange({ target: { name: 'dateTo', value: '' } });
                                    }}
                                    color="secondary"
                                    size="small"
                                />
                            )}
                            {filters.min_amount && (
                                <Chip
                                    label={`Min Amount: ${formatCurrency(filters.min_amount)}`}
                                    onDelete={() => handleFilterChange({ target: { name: 'min_amount', value: '' } })}
                                    color="success"
                                    size="small"
                                />
                            )}
                            {filters.max_amount && (
                                <Chip
                                    label={`Max Amount: ${formatCurrency(filters.max_amount)}`}
                                    onDelete={() => handleFilterChange({ target: { name: 'max_amount', value: '' } })}
                                    color="error"
                                    size="small"
                                />
                            )}
                        </Box>
                    </Box>
                )}
            </Paper>

            {/* Report Summary Cards */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                {/* Total Bookings */}
                <Grid item xs={6} sm={4} md={3} lg={2}>
                    <Card elevation={3}>
                        <CardContent sx={{ textAlign: 'center' }}>
                            <Hotel sx={{ fontSize: 40, color: theme.palette.primary.main, mb: 1 }} />
                            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                {formatNumberWithComma(reportData.summary.total_bookings || 0)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Total Bookings
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Total Revenue */}
                <Grid item xs={6} sm={4} md={3} lg={2}>
                    <Card elevation={3}>
                        <CardContent sx={{ textAlign: 'center' }}>
                            <AttachMoney sx={{ fontSize: 40, color: theme.palette.success.main, mb: 1 }} />
                            <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.success.main }}>
                                {formatCurrency(reportData.summary.total_revenue || 0)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Total Revenue
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Total Advance */}
                <Grid item xs={6} sm={4} md={3} lg={2}>
                    <Card elevation={3}>
                        <CardContent sx={{ textAlign: 'center' }}>
                            <Payment sx={{ fontSize: 40, color: theme.palette.info.main, mb: 1 }} />
                            <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.info.main }}>
                                {formatCurrency(reportData.summary.total_advance || 0)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Total Advance
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Balance Due */}
                <Grid item xs={6} sm={4} md={3} lg={2}>
                    <Card elevation={3}>
                        <CardContent sx={{ textAlign: 'center' }}>
                            <AccountBalance sx={{ fontSize: 40, color: theme.palette.warning.main, mb: 1 }} />
                            <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.warning.main }}>
                                {formatCurrency(reportData.summary.total_balance || 0)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Balance Due
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Average Booking Value */}
                <Grid item xs={6} sm={4} md={3} lg={2}>
                    <Card elevation={3}>
                        <CardContent sx={{ textAlign: 'center' }}>
                            <Assessment sx={{ fontSize: 40, color: theme.palette.secondary.main, mb: 1 }} />
                            <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.secondary.main }}>
                                {formatCurrency(reportData.summary.average_booking_value || 0)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Avg Booking Value
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Occupancy Rate */}
                <Grid item xs={6} sm={4} md={3} lg={2}>
                    <Card elevation={3}>
                        <CardContent sx={{ textAlign: 'center' }}>
                            <TrendingUp sx={{ fontSize: 40, color: theme.palette.primary.main, mb: 1 }} />
                            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                {reportData.summary.occupancy_rate || 0}%
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Occupancy Rate
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Individual Bookings */}
                <Grid item xs={6} sm={4} md={3} lg={2}>
                    <Card elevation={3}>
                        <CardContent sx={{ textAlign: 'center' }}>
                            <Person sx={{ fontSize: 40, color: theme.palette.info.main, mb: 1 }} />
                            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                {formatNumberWithComma(reportData.summary.individual_bookings || 0)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Individual
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Organisation Bookings */}
                <Grid item xs={6} sm={4} md={3} lg={2}>
                    <Card elevation={3}>
                        <CardContent sx={{ textAlign: 'center' }}>
                            <Business sx={{ fontSize: 40, color: theme.palette.warning.main, mb: 1 }} />
                            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                {formatNumberWithComma(reportData.summary.organisation_bookings || 0)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Organisation
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Checked In */}
                <Grid item xs={6} sm={4} md={3} lg={2}>
                    <Card elevation={3}>
                        <CardContent sx={{ textAlign: 'center' }}>
                            <CheckCircle sx={{ fontSize: 40, color: theme.palette.success.main, mb: 1 }} />
                            <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.success.main }}>
                                {formatNumberWithComma(reportData.summary.checked_in_bookings || 0)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Checked In
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Checked Out */}
                <Grid item xs={6} sm={4} md={3} lg={2}>
                    <Card elevation={3}>
                        <CardContent sx={{ textAlign: 'center' }}>
                            <EventSeat sx={{ fontSize: 40, color: theme.palette.info.main, mb: 1 }} />
                            <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.info.main }}>
                                {formatNumberWithComma(reportData.summary.checked_out_bookings || 0)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Checked Out
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Active */}
                <Grid item xs={6} sm={4} md={3} lg={2}>
                    <Card elevation={3}>
                        <CardContent sx={{ textAlign: 'center' }}>
                            <Event sx={{ fontSize: 40, color: theme.palette.primary.main, mb: 1 }} />
                            <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                                {formatNumberWithComma(reportData.summary.active_bookings || 0)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Active
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Cancelled */}
                <Grid item xs={6} sm={4} md={3} lg={2}>
                    <Card elevation={3}>
                        <CardContent sx={{ textAlign: 'center' }}>
                            <Error sx={{ fontSize: 40, color: theme.palette.error.main, mb: 1 }} />
                            <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.error.main }}>
                                {formatNumberWithComma(reportData.summary.cancelled_bookings || 0)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Cancelled
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Report Tabs */}
            <Paper elevation={2} sx={{ borderRadius: 3, overflow: 'hidden' }}>
                <Tabs
                    value={activeTab}
                    onChange={handleTabChange}
                    variant="fullWidth"
                    sx={{ bgcolor: theme.palette.primary.main }}
                >
                    <Tab
                        label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'white' }}>
                                <Summarize /> Detailed Report
                            </Box>
                        }
                    />
                    <Tab
                        label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'white' }}>
                                <BarChart /> Analysis
                            </Box>
                        }
                    />
                    <Tab
                        label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'white' }}>
                                <People /> Customers
                            </Box>
                        }
                    />
                    <Tab
                        label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'white' }}>
                                <Analytics /> Insights
                            </Box>
                        }
                    />
                </Tabs>

                {/* Tab 1: Detailed Report */}
                {activeTab === 0 && (
                    <Box sx={{ p: 3 }}>
                        <Typography variant="h6" sx={{ mb: 3 }}>
                            Detailed Booking Report
                        </Typography>

                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow sx={{ bgcolor: theme.palette.action.hover }}>
                                        <TableCell><strong>#</strong></TableCell>
                                        <TableCell><strong>Booking #</strong></TableCell>
                                        <TableCell><strong>Customer/Organisation</strong></TableCell>
                                        <TableCell><strong>Type</strong></TableCell>
                                        <TableCell><strong>Check In</strong></TableCell>
                                        <TableCell><strong>Check Out</strong></TableCell>
                                        <TableCell><strong>Room</strong></TableCell>
                                        <TableCell><strong>Guests</strong></TableCell>
                                        <TableCell><strong>Total</strong></TableCell>
                                        <TableCell><strong>Advance</strong></TableCell>
                                        <TableCell><strong>Balance</strong></TableCell>
                                        <TableCell><strong>Payment</strong></TableCell>
                                        <TableCell><strong>Status</strong></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {reportData.details?.map((booking, index) => {
                                        const balance = (booking.total_amount || 0) - (booking.advance_amount || 0);
                                        const paymentStatus = getPaymentStatusText(booking);
                                        const paymentColor = getPaymentStatusColor(booking);

                                        return (
                                            <TableRow key={booking.id || index} hover>
                                                <TableCell>{index + 1}</TableCell>
                                                <TableCell sx={{ fontWeight: 'medium' }}>
                                                    {booking.booking_no || `BK${booking.id}`}
                                                </TableCell>
                                                <TableCell>
                                                    <Box>
                                                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                                            {getCustomerDisplay(booking)}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {getCustomerContact(booking)}
                                                        </Typography>
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        icon={booking.booking_type === 1 ? <Person /> : <Business />}
                                                        label={booking.booking_type === 1 ? 'Individual' : 'Organisation'}
                                                        size="small"
                                                        color={booking.booking_type === 1 ? 'default' : 'secondary'}
                                                        variant="outlined"
                                                    />
                                                </TableCell>
                                                <TableCell>{formatCreatedAtDate(booking.check_in)}</TableCell>
                                                <TableCell>{formatCreatedAtDate(booking.check_out)}</TableCell>
                                                <TableCell>
                                                    {getCategoriesNameById(booking.room_type, referenceData.roomNames)}
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={booking.total_guests || 1}
                                                        size="small"
                                                        color="info"
                                                        icon={<People />}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" sx={{ fontWeight: 'medium', color: theme.palette.primary.main }}>
                                                        {formatCurrency(booking.total_amount || 0)}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" color="success.main">
                                                        {formatCurrency(booking.advance_amount || 0)}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" color="warning.main">
                                                        {formatCurrency(balance)}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={paymentStatus}
                                                        size="small"
                                                        sx={{ bgcolor: paymentColor, color: 'white' }}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={getStatusById(booking.status)}
                                                        size="small"
                                                        sx={{ bgcolor: getStatusColor(booking.status), color: 'white' }}
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}

                                    {/* Footer Row with Grand Totals */}
                                    {reportData.details && reportData.details.length > 0 && (
                                        <TableRow sx={{
                                            backgroundColor: theme.palette.primary.dark,
                                            '& td': {
                                                color: 'white',
                                                fontWeight: 'bold',
                                                fontSize: '1rem',
                                                borderTop: `3px solid ${theme.palette.primary.main}`,
                                                padding: '16px 8px'
                                            }
                                        }}>
                                            <TableCell colSpan={8} align="center">
                                                <Typography variant="h6" sx={{ color: 'white' }}>
                                                    GRAND TOTAL
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="h6" sx={{ color: 'white' }}>
                                                    {formatCurrency(
                                                        reportData.details.reduce((sum, item) => sum + (parseFloat(item.total_amount) || 0), 0)
                                                    )}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="h6" sx={{ color: 'white' }}>
                                                    {formatCurrency(
                                                        reportData.details.reduce((sum, item) => sum + (parseFloat(item.advance_amount) || 0), 0)
                                                    )}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="h6" sx={{ color: theme.palette.warning.light }}>
                                                    {formatCurrency(
                                                        reportData.details.reduce((sum, item) => sum + ((parseFloat(item.total_amount) || 0) - (parseFloat(item.advance_amount) || 0)), 0)
                                                    )}
                                                </Typography>
                                            </TableCell>
                                            <TableCell colSpan={2}></TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        {(!reportData.details || reportData.details.length === 0) && (
                            <Box sx={{ textAlign: 'center', py: 6 }}>
                                <Hotel sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                                <Typography variant="h6" color="text.secondary">
                                    No bookings found
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Try adjusting your filters
                                </Typography>
                            </Box>
                        )}
                    </Box>
                )}

                {/* Tab 2: Analysis */}
                {activeTab === 1 && (
                    <Box sx={{ p: 3 }}>
                        <Typography variant="h6" sx={{ mb: 3 }}>
                            Booking Analysis
                        </Typography>

                        <Grid container spacing={3}>
                            {/* Revenue by Status */}
                            <Grid item xs={12} md={6}>
                                <Card elevation={2}>
                                    <CardContent>
                                        <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Assessment /> Revenue by Status
                                        </Typography>
                                        {reportData.revenue_by_status?.map((item, index) => (
                                            <Box key={index} sx={{ mb: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                    <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                                                        {getStatusById(item.status)}
                                                    </Typography>
                                                    <Typography variant="h6" color="primary">
                                                        {formatCurrency(item.total_revenue || 0)}
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                                                    <Typography variant="body2" color="text.secondary">
                                                        Bookings: {formatNumberWithComma(item.booking_count)}
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        Avg: {formatCurrency(item.avg_booking_value || 0)}
                                                    </Typography>
                                                </Box>
                                                <LinearProgress
                                                    variant="determinate"
                                                    value={((item.total_revenue || 0) / (reportData.revenue_by_status?.[0]?.total_revenue || 1)) * 100}
                                                    sx={{ mt: 1, height: 8, borderRadius: 4 }}
                                                />
                                            </Box>
                                        ))}
                                    </CardContent>
                                </Card>
                            </Grid>

                            {/* Bookings by Type */}
                            <Grid item xs={12} md={6}>
                                <Card elevation={2}>
                                    <CardContent>
                                        <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <PieChart /> Bookings by Type
                                        </Typography>
                                        {reportData.bookings_by_type?.map((item, index) => (
                                            <Box key={index} sx={{ mb: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        {item.booking_type === 1 ? <Person color="primary" /> : <Business color="secondary" />}
                                                        <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                                                            {item.booking_type === 1 ? 'Individual' : 'Organisation'}
                                                        </Typography>
                                                    </Box>
                                                    <Typography variant="h6" color="primary">
                                                        {formatCurrency(item.total_revenue || 0)}
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                                                    <Typography variant="body2" color="text.secondary">
                                                        Bookings: {formatNumberWithComma(item.count)}
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        Advance: {formatCurrency(item.total_advance || 0)}
                                                    </Typography>
                                                </Box>
                                                <LinearProgress
                                                    variant="determinate"
                                                    value={((item.total_revenue || 0) / (reportData.bookings_by_type?.[0]?.total_revenue || 1)) * 100}
                                                    sx={{ mt: 1, height: 8, borderRadius: 4 }}
                                                />
                                            </Box>
                                        ))}
                                    </CardContent>
                                </Card>
                            </Grid>

                            {/* Monthly Trends */}
                            <Grid item xs={12}>
                                <Card elevation={2}>
                                    <CardContent>
                                        <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <TrendingUp /> Monthly Trends
                                        </Typography>
                                        <TableContainer>
                                            <Table size="small">
                                                <TableHead>
                                                    <TableRow sx={{ bgcolor: theme.palette.action.hover }}>
                                                        <TableCell><strong>Month</strong></TableCell>
                                                        <TableCell align="right"><strong>Bookings</strong></TableCell>
                                                        <TableCell align="right"><strong>Individual</strong></TableCell>
                                                        <TableCell align="right"><strong>Organisation</strong></TableCell>
                                                        <TableCell align="right"><strong>Revenue</strong></TableCell>
                                                        <TableCell align="right"><strong>Advance</strong></TableCell>
                                                        <TableCell align="right"><strong>Avg Value</strong></TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {reportData.monthly_trends?.map((trend, index) => (
                                                        <TableRow key={index} hover>
                                                            <TableCell>{trend.month}</TableCell>
                                                            <TableCell align="right">{formatNumberWithComma(trend.booking_count)}</TableCell>
                                                            <TableCell align="right">{formatNumberWithComma(trend.individual_bookings || 0)}</TableCell>
                                                            <TableCell align="right">{formatNumberWithComma(trend.organisation_bookings || 0)}</TableCell>
                                                            <TableCell align="right" sx={{ color: theme.palette.success.main, fontWeight: 'medium' }}>
                                                                {formatCurrency(trend.total_revenue)}
                                                            </TableCell>
                                                            <TableCell align="right" sx={{ color: theme.palette.info.main }}>
                                                                {formatCurrency(trend.total_advance)}
                                                            </TableCell>
                                                            <TableCell align="right">{formatCurrency(trend.avg_booking_value)}</TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>
                    </Box>
                )}

                {/* Tab 3: Customers */}
                {activeTab === 2 && (
                    <Box sx={{ p: 3 }}>
                        <Typography variant="h6" sx={{ mb: 3 }}>
                            Customer Analysis
                        </Typography>

                        <Grid container spacing={3}>
                            {/* Top Customers */}
                            <Grid item xs={12} md={6}>
                                <Card elevation={2}>
                                    <CardContent>
                                        <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Star /> Top Individual Customers
                                        </Typography>
                                        {reportData.top_customers?.map((customer, index) => (
                                            <Box key={index} sx={{ mb: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                    <Box>
                                                        <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                                                            {customer.customer_name}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {customer.customer_mobile}
                                                        </Typography>
                                                    </Box>
                                                    <Typography variant="h6" color="primary">
                                                        {formatCurrency(customer.total_spent)}
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                                                    <Typography variant="body2" color="text.secondary">
                                                        Bookings: {customer.booking_count}
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        Last: {customer.last_booking ? formatCreatedAtDate(customer.last_booking) : 'N/A'}
                                                    </Typography>
                                                </Box>
                                                <LinearProgress
                                                    variant="determinate"
                                                    value={((customer.total_spent || 0) / (reportData.top_customers?.[0]?.total_spent || 1)) * 100}
                                                    sx={{ mt: 1, height: 8, borderRadius: 4 }}
                                                />
                                            </Box>
                                        ))}
                                    </CardContent>
                                </Card>
                            </Grid>

                            {/* Top Organisations */}
                            <Grid item xs={12} md={6}>
                                <Card elevation={2}>
                                    <CardContent>
                                        <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Business /> Top Organisations
                                        </Typography>
                                        {reportData.top_organisations?.map((org, index) => (
                                            <Box key={index} sx={{ mb: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                    <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                                                        {org.organisation_name}
                                                    </Typography>
                                                    <Typography variant="h6" color="secondary">
                                                        {formatCurrency(org.total_spent)}
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                                                    <Typography variant="body2" color="text.secondary">
                                                        Bookings: {org.booking_count}
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        Members: {org.total_members || 0}
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                                                    <Typography variant="body2" color="text.secondary">
                                                        Advance: {formatCurrency(org.total_advance)}
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        Last: {org.last_booking ? formatCreatedAtDate(org.last_booking) : 'N/A'}
                                                    </Typography>
                                                </Box>
                                                <LinearProgress
                                                    variant="determinate"
                                                    value={((org.total_spent || 0) / (reportData.top_organisations?.[0]?.total_spent || 1)) * 100}
                                                    sx={{ mt: 1, height: 8, borderRadius: 4 }}
                                                />
                                            </Box>
                                        ))}
                                    </CardContent>
                                </Card>
                            </Grid>

                            {/* Recent Activity */}
                            <Grid item xs={12}>
                                <Card elevation={2}>
                                    <CardContent>
                                        <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <AccessTime /> Recent Activity
                                        </Typography>
                                        <TableContainer>
                                            <Table size="small">
                                                <TableHead>
                                                    <TableRow sx={{ bgcolor: theme.palette.action.hover }}>
                                                        <TableCell><strong>Booking #</strong></TableCell>
                                                        <TableCell><strong>Customer/Org</strong></TableCell>
                                                        <TableCell><strong>Type</strong></TableCell>
                                                        <TableCell><strong>Status</strong></TableCell>
                                                        <TableCell><strong>Amount</strong></TableCell>
                                                        <TableCell><strong>Last Updated</strong></TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {reportData.recent_activity?.map((activity, index) => (
                                                        <TableRow key={index} hover>
                                                            <TableCell sx={{ fontWeight: 'medium' }}>
                                                                {activity.booking_no || `BK${activity.id}`}
                                                            </TableCell>
                                                            <TableCell>
                                                                {activity.booking_type === 2 ? activity.organisation_name : activity.customer_name}
                                                            </TableCell>
                                                            <TableCell>
                                                                <Chip
                                                                    size="small"
                                                                    icon={activity.booking_type === 1 ? <Person /> : <Business />}
                                                                    label={activity.booking_type === 1 ? 'Individual' : 'Organisation'}
                                                                    variant="outlined"
                                                                />
                                                            </TableCell>
                                                            <TableCell>
                                                                <Chip
                                                                    size="small"
                                                                    label={getStatusById(activity.status)}
                                                                    sx={{ bgcolor: getStatusColor(activity.status), color: 'white' }}
                                                                />
                                                            </TableCell>
                                                            <TableCell sx={{ color: theme.palette.primary.main }}>
                                                                {formatCurrency(activity.total_amount)}
                                                            </TableCell>
                                                            <TableCell>
                                                                {activity.updated_at ? formatCreatedAtDate(activity.updated_at) : 'N/A'}
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>
                    </Box>
                )}

                {/* Tab 4: Insights */}
                {activeTab === 3 && (
                    <Box sx={{ p: 3 }}>
                        <Typography variant="h6" sx={{ mb: 3 }}>
                            Business Insights
                        </Typography>

                        <Grid container spacing={3}>
                            {/* Profitability Insights */}
                            <Grid item xs={12} md={6}>
                                <Card elevation={2}>
                                    <CardContent>
                                        <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Assessment /> Revenue Analysis
                                        </Typography>

                                        <Box sx={{ mb: 3 }}>
                                            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                                                Advance vs Balance Ratio
                                            </Typography>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                <Box sx={{ flex: 1 }}>
                                                    <LinearProgress
                                                        variant="determinate"
                                                        value={
                                                            reportData.summary.total_revenue > 0
                                                                ? (reportData.summary.total_advance / reportData.summary.total_revenue) * 100
                                                                : 0
                                                        }
                                                        color="success"
                                                        sx={{ height: 10, borderRadius: 5 }}
                                                    />
                                                </Box>
                                                <Typography variant="h6" sx={{ minWidth: 80 }}>
                                                    {reportData.summary.total_revenue > 0
                                                        ? `${((reportData.summary.total_advance / reportData.summary.total_revenue) * 100).toFixed(1)}%`
                                                        : '0%'
                                                    }
                                                </Typography>
                                            </Box>
                                        </Box>

                                        {/* Average Metrics */}
                                        <Grid container spacing={2}>
                                            <Grid item xs={6}>
                                                <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Avg Booking Value
                                                    </Typography>
                                                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                                                        {formatCurrency(reportData.summary.average_booking_value || 0)}
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                            <Grid item xs={6}>
                                                <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Occupancy Rate
                                                    </Typography>
                                                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.success.main }}>
                                                        {reportData.summary.occupancy_rate || 0}%
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                            <Grid item xs={6}>
                                                <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Avg Advance
                                                    </Typography>
                                                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.info.main }}>
                                                        {reportData.summary.total_bookings > 0
                                                            ? formatCurrency(reportData.summary.total_advance / reportData.summary.total_bookings)
                                                            : formatCurrency(0)
                                                        }
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                            <Grid item xs={6}>
                                                <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Avg Balance
                                                    </Typography>
                                                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.warning.main }}>
                                                        {reportData.summary.total_bookings > 0
                                                            ? formatCurrency(reportData.summary.total_balance / reportData.summary.total_bookings)
                                                            : formatCurrency(0)
                                                        }
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                        </Grid>
                                    </CardContent>
                                </Card>
                            </Grid>

                            {/* Efficiency Insights */}
                            <Grid item xs={12} md={6}>
                                <Card elevation={2}>
                                    <CardContent>
                                        <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Analytics /> Booking Metrics
                                        </Typography>

                                        {/* Distribution */}
                                        <Box sx={{ mb: 3 }}>
                                            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                                                Booking Distribution
                                            </Typography>
                                            <Grid container spacing={1}>
                                                <Grid item xs={3}>
                                                    <Box sx={{ p: 1, bgcolor: theme.palette.primary.light, borderRadius: 1, textAlign: 'center' }}>
                                                        <Typography variant="caption" display="block">
                                                        Individual
                                                        </Typography>
                                                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                                            {reportData.summary.total_bookings > 0
                                                                ? `${((reportData.summary.individual_bookings / reportData.summary.total_bookings) * 100).toFixed(1)}%`
                                                                : '0%'
                                                            }
                                                        </Typography>
                                                    </Box>
                                                </Grid>
                                                <Grid item xs={3}>
                                                    <Box sx={{ p: 1, bgcolor: theme.palette.secondary.light, borderRadius: 1, textAlign: 'center' }}>
                                                        <Typography variant="caption" display="block">
                                                            Organisation
                                                        </Typography>
                                                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                                            {reportData.summary.total_bookings > 0
                                                                ? `${((reportData.summary.organisation_bookings / reportData.summary.total_bookings) * 100).toFixed(1)}%`
                                                                : '0%'
                                                            }
                                                        </Typography>
                                                    </Box>
                                                </Grid>
                                                <Grid item xs={3}>
                                                    <Box sx={{ p: 1, bgcolor: theme.palette.success.light, borderRadius: 1, textAlign: 'center' }}>
                                                        <Typography variant="caption" display="block">
                                                            Checked In
                                                        </Typography>
                                                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                                            {reportData.summary.total_bookings > 0
                                                                ? `${((reportData.summary.checked_in_bookings / reportData.summary.total_bookings) * 100).toFixed(1)}%`
                                                                : '0%'
                                                            }
                                                        </Typography>
                                                    </Box>
                                                </Grid>
                                                <Grid item xs={3}>
                                                    <Box sx={{ p: 1, bgcolor: theme.palette.warning.light, borderRadius: 1, textAlign: 'center' }}>
                                                        <Typography variant="caption" display="block">
                                                            Active
                                                        </Typography>
                                                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                                            {reportData.summary.total_bookings > 0
                                                                ? `${((reportData.summary.active_bookings / reportData.summary.total_bookings) * 100).toFixed(1)}%`
                                                                : '0%'
                                                            }
                                                        </Typography>
                                                    </Box>
                                                </Grid>
                                            </Grid>
                                        </Box>

                                        {/* Recommendations */}
                                        <Box sx={{ p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
                                            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                                                Key Insights
                                            </Typography>
                                            <ul style={{ margin: 0, paddingLeft: 20 }}>
                                                {reportData.summary.checked_in_bookings > 0 && (
                                                    <li>
                                                        <Typography variant="body2">
                                                            Currently {reportData.summary.checked_in_bookings} guests checked in
                                                        </Typography>
                                                    </li>
                                                )}
                                                {reportData.summary.organisation_bookings > reportData.summary.individual_bookings && (
                                                    <li>
                                                        <Typography variant="body2">
                                                            Organisation bookings are leading ({reportData.summary.organisation_bookings} vs {reportData.summary.individual_bookings} individual)
                                                        </Typography>
                                                    </li>
                                                )}
                                                {reportData.summary.total_balance > 0 && (
                                                    <li>
                                                        <Typography variant="body2">
                                                            Outstanding balance of {formatCurrency(reportData.summary.total_balance)} to be collected
                                                        </Typography>
                                                    </li>
                                                )}
                                                {reportData.top_customers?.[0] && (
                                                    <li>
                                                        <Typography variant="body2">
                                                            Top customer: {reportData.top_customers[0].customer_name} ({formatCurrency(reportData.top_customers[0].total_spent)})
                                                        </Typography>
                                                    </li>
                                                )}
                                                {reportData.top_organisations?.[0] && (
                                                    <li>
                                                        <Typography variant="body2">
                                                            Top organisation: {reportData.top_organisations[0].organisation_name}
                                                        </Typography>
                                                    </li>
                                                )}
                                            </ul>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>
                    </Box>
                )}
            </Paper>

            {/* Export Menu */}
            <Menu
                anchorEl={exportAnchorEl}
                open={Boolean(exportAnchorEl)}
                onClose={handleExportClose}
            >
                <MenuItem onClick={handleExportExcel} sx={{ gap: 1 }}>
                    <TableChart fontSize="small" color="success" />
                    Export to Excel (.xlsx)
                </MenuItem>
                <MenuItem onClick={handleExportPDF} sx={{ gap: 1 }}>
                    <PictureAsPdf fontSize="small" color="error" />
                    Export to PDF (.pdf)
                </MenuItem>
                <MenuItem onClick={handleExportCSV} sx={{ gap: 1 }}>
                    <TableChart fontSize="small" color="info" />
                    Export to CSV (.csv)
                </MenuItem>
            </Menu>
        </Container>
    );
};

export default BookingReport;

